/* ==========================================================================
   Provision Modal
   Task F36: Confirm provisioning with optional slug override
   ========================================================================== */

import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { FormField } from '@/components/ui/FormField'
import { Alert } from '@/components/ui/Alert'
import { provisionFromIntake, type IntakeDetail } from '@/api/internal'
import { ApiClientError } from '@/api/client'

interface ProvisionModalProps {
  open: boolean
  onClose: () => void
  intake: IntakeDetail
  onSuccess: () => void
}

export function ProvisionModal({
  open,
  onClose,
  intake,
  onSuccess,
}: ProvisionModalProps) {
  const [slug, setSlug] = useState(intake.preferred_slug)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [success, setSuccess] = useState(false)

  const handleProvision = async () => {
    setLoading(true)
    setError(undefined)

    try {
      const slugOverride =
        slug !== intake.preferred_slug ? slug : undefined
      await provisionFromIntake(intake.id, slugOverride)
      setSuccess(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.getUserMessage())
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (success) {
      onSuccess()
    }
    setSuccess(false)
    setError(undefined)
    setSlug(intake.preferred_slug)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} title="Provision Account" size="md">
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {success ? (
        <div>
          <Alert variant="success" className="mb-4">
            Pharmacy provisioned successfully.
          </Alert>
          <p className="text-sm text-charcoal/70 mb-4">
            Use &quot;Open Admin Portal&quot; to configure catalog, settings, and branding
            before completing onboarding.
          </p>
          <Button variant="primary" onClick={handleClose} className="w-full">
            Done
          </Button>
        </div>
      ) : (
        <div>
          {/* Preview */}
          <div className="space-y-3 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-charcoal/60">Pharmacy</span>
              <span className="font-medium text-charcoal">{intake.pharmacy_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-charcoal/60">Admin Email</span>
              <span className="text-charcoal">{intake.admin_email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-charcoal/60">Tier</span>
              <span className="text-charcoal capitalize">{intake.tier || '—'}</span>
            </div>
          </div>

          <FormField label="Portal Slug" htmlFor="provision-slug">
            <Input
              id="provision-slug"
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value)
                setError(undefined)
              }}
              disabled={loading}
              className="font-mono text-sm"
            />
            <p className="text-xs text-charcoal/50 mt-1">
              Portal URL: /rx/{slug}/
            </p>
          </FormField>

          <div className="flex gap-3 mt-6">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleProvision}
              loading={loading}
              disabled={loading}
              className="flex-1"
            >
              Provision
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default ProvisionModal
